import React from "react";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  Box,
  PhoneIcon,
  Typography,
  UserIcon
} from "@dealer-center/core-components";
import {
  CONTAINER_STYLES,
  ICON_BOX_STYLES,
  TYPOGRAPHY_STYLES
} from "../styleConstants";
import { ContactDetailItem } from "./ContactDetailItem";

// Map icon keys to Interstate icon components
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  phone: PhoneIcon,
  user: UserIcon
};

interface ContactData {
  name?: string;
  comments?: string;
  phoneNumbers?: { value: string; label?: string }[];
  emails?: { value: string; label?: string }[];
  urls?: { value: string; label?: string }[];
}

interface SupportCardProps {
  title: string;
  iconKey: string;
  iconBackgroundColor: string;
  contactData?: ContactData;
  callCoxLabel?: string;
  children?: React.ReactNode;
}

export const SupportCard: React.FC<SupportCardProps> = ({
  title,
  iconKey,
  iconBackgroundColor,
  contactData,
  callCoxLabel,
  children
}) => {
  const Icon = ICON_MAP[iconKey];

  if (!Icon) {
    return null;
  }

  // Render contact details based on available data
  const renderContactDetails = () => {
    if (!contactData) return null;

    return (
      <>
        {/* Name (for Account Manager) */}
        {contactData.name && (
          <Typography sx={TYPOGRAPHY_STYLES.bodyBold} variant="body-md">
            {contactData.name}
          </Typography>
        )}

        {/* Availability/Comments (for Technical Support) */}
        {contactData.comments && (
          <ContactDetailItem
            iconColor="#333"
            iconKey="clock"
            iconTitle="Availability hours"
            prefix={String(Locale.getText("AVAILABLE"))}
            textColor="#333"
            textWeight={500}
            value={contactData.comments}
          />
        )}

        {/* Call Cox label (always shown when provided, shown first) */}
        {callCoxLabel && (
          <ContactDetailItem
            iconColor="#666"
            iconKey="phone"
            iconTitle="Call Cox"
            value={callCoxLabel}
          />
        )}

        {/* Phone Numbers */}
        {contactData.phoneNumbers?.map(
          (phone: { value: string; label?: string }, i: number) =>
            phone?.value ? (
              <ContactDetailItem
                iconColor="#666"
                iconKey="phone"
                iconTitle="Phone number"
                key={phone.value}
                suffix={phone.label ?? String(Locale.getText("CC_CALL"))}
                value={phone.value}
              />
            ) : null
        )}

        {/* Emails */}
        {contactData.emails && contactData.emails[0]?.value && (
          <ContactDetailItem
            href={`mailto:${contactData.emails[0].value}`}
            iconColor="#009E77"
            iconKey="email"
            iconTitle="Email address"
            textColor="#009E77"
            value={contactData.emails[0].value}
          />
        )}

        {/* URLs */}
        {contactData.urls?.map(
          (url: { value: string; label?: string }, i: number) =>
            url?.value ? (
              <ContactDetailItem
                href={url.value}
                iconColor="#009E77"
                iconKey="openInNewTab"
                iconTitle="Open in new tab"
                isExternal={true}
                key={url.value}
                label={url.label || String(Locale.getText("HELP_CENTER"))}
                textColor="#009E77"
                value={url.value}
              />
            ) : null
        )}
      </>
    );
  };
  return (
    <Box
      sx={{
        flex: { xs: "1 1 100%", sm: "0 1 calc(50% - 8px)" },
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        className="support-card"
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #999",
          boxSizing: "border-box",
          padding: "20px",
          width: "100%",
          minWidth: 254,
          maxWidth: "100%",
          minHeight: 180,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,1)"
          }
        }}
      >
        <Box
          className="support-header"
          sx={{
            ...CONTAINER_STYLES.flexRowCenter,
            marginBottom: "12px"
          }}
        >
          <Box
            className="support-icon"
            sx={{
              ...ICON_BOX_STYLES.base,
              backgroundColor: iconBackgroundColor
            }}
          >
            <Icon
              style={{
                fontSize: "20px",
                lineHeight: "30px"
              }}
              title={title}
            />
          </Box>
          <Typography sx={TYPOGRAPHY_STYLES.cardHeader} variant="h6">
            {title}
          </Typography>
        </Box>

        {(contactData || children) && (
          <Box sx={CONTAINER_STYLES.supportDetails}>
            {renderContactDetails()}
            {children}
          </Box>
        )}
      </Box>
    </Box>
  );
};
